import type { JSX } from 'react';
import { cn } from '../lib/cn.js';
import type { ViewId, ViewOption } from './contract.js';

/**
 * LE SELECTEUR DE VUES : rangee 3c, groupe EPINGLE a droite.
 *
 * Segmented control dense. LE GABARIT LUI PASSE LE NIVEAU OU L'ON EST, JAMAIS
 * LES DEUX : au niveau collection les vues de collection (liste, tableau,
 * kanban, calendrier), car on n'entre pas dans une fiche en CHOISISSANT une
 * vue, on entre en OUVRANT une ligne ; au niveau fiche, a la meme place, les
 * vues DE FICHE (« Fiche » en tete, puis les vues Canvas et HTML du jeu,
 * chantier `fiche-suivi`). `ScreenLayout` filtre a l'appel (`collectionViews`,
 * `vuesDeFicheEnOrdre`).
 *
 * LE GROUPEMENT EN DEUX NIVEAUX, avec son filet, RESTE ECRIT ICI : c'est une
 * primitive, d'autres selecteurs a deux groupes s'en servent. Ce qui a disparu
 * est son USAGE par le gabarit, pas la capacite du composant.
 *
 * Trois regles portantes :
 *
 * - UNE ENTREE NON SATISFAITE EST GRISEE, JAMAIS ABSENTE. Sa raison s'affiche
 *   au survol (`title`) : le developpeur voit ce qu'il rate et ce qu'il doit
 *   declarer pour la debloquer. On utilise `aria-disabled` et pas `disabled`,
 *   justement pour que l'entree reste survolable et atteignable au clavier.
 * - LE LIME N'ENTRE PAS ICI. Le bouton lime unique appartient a l'ecran
 *   (rangee 3a). L'entree active est encree, pas accentuee.
 * - CA TIENT AVEC 2 COMME AVEC 6 ENTREES : icone seule, libelle en `title` et
 *   en `aria-label`. La largeur du groupe epingle reste constante et previsible,
 *   c'est le groupe defilant (recherche + pastilles) qui absorbe le reste.
 */

export interface ViewSelectorProps {
  /** Les entrees, dans l'ordre declare par l'ecran. Le regroupement est fait ici. */
  views: ViewOption[];
  /** L'entree active. */
  activeViewId?: ViewId;
  /** Selection d'une entree (les entrees grisees n'appellent jamais). */
  onViewSelect?: (id: ViewId) => void;
}

/** Un bouton du segmented control. */
function SelectorItem({
  view,
  active,
  onSelect,
}: {
  view: ViewOption;
  active: boolean;
  onSelect?: (id: ViewId) => void;
}): JSX.Element {
  const disabled = view.disabled;
  return (
    <button
      type="button"
      aria-label={view.label}
      aria-pressed={active}
      aria-disabled={disabled || undefined}
      title={disabled && view.reason != null ? `${view.label} : ${view.reason}` : view.label}
      onClick={disabled ? undefined : () => onSelect?.(view.id)}
      className={cn(
        'flex h-7 w-8 shrink-0 items-center justify-center rounded-[0.5rem] transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-border',
        disabled
          ? 'cursor-not-allowed text-ink-300'
          : active
            ? 'bg-surface text-ink-900 shadow-card'
            : 'text-ink-500 hover:text-ink-900',
      )}
    >
      {view.icon}
    </button>
  );
}

/**
 * Rend le selecteur. UNE SEULE REGLE, aux deux niveaux : MOINS DE DEUX
 * ENTREES, IL NE RENDRA RIEN, il n'y a rien a choisir (une entree grisee
 * compte : elle reste « grisee, jamais absente », donc deux entrees dont une
 * grisee rendent le groupe). On cache le selecteur, jamais une vue.
 */
export function ViewSelector({ views, activeViewId, onViewSelect }: ViewSelectorProps): JSX.Element | null {
  if (views.length < 2) return null;

  const collection = views.filter((view) => view.level === 'collection');
  const record = views.filter((view) => view.level === 'record');

  return (
    <div
      role="group"
      aria-label="Type de vue"
      className="flex h-8 shrink-0 items-center gap-0.5 rounded-[0.625rem] border border-border bg-surface-1 px-0.5"
    >
      {collection.map((view) => (
        <SelectorItem
          key={view.id}
          view={view}
          active={view.id === activeViewId}
          {...(onViewSelect ? { onSelect: onViewSelect } : {})}
        />
      ))}
      {collection.length > 0 && record.length > 0 && (
        /* Le filet qui separe les deux NIVEAUX : collection a gauche, fiche a droite. */
        <span aria-hidden className="mx-1 h-4 w-px shrink-0 bg-border" />
      )}
      {record.map((view) => (
        <SelectorItem
          key={view.id}
          view={view}
          active={view.id === activeViewId}
          {...(onViewSelect ? { onSelect: onViewSelect } : {})}
        />
      ))}
    </div>
  );
}
